// Shared face + effect shapes, drawn in a 200×222 local box (eyes at y=66).
const INK = "#2b0b10";
const L = [74, 66];
const R = [126, 66];
const mx = (x) => 200 - x;

const round = { strokeLinecap: "round", strokeLinejoin: "round", fill: "none", stroke: INK };

function Eye({ c: [cx, cy], type, dart }) {
  switch (type) {
    case "open":
      return (
        <>
          <ellipse cx={cx} cy={cy} rx="11" ry="14.5" fill={INK} />
          <circle cx={cx + 3.5} cy={cy - 5} r="4" fill="#fff" />
          <circle cx={cx - 3} cy={cy + 5} r="1.8" fill="#fff" />
        </>
      );
    case "sad":
      return (
        <>
          <ellipse cx={cx} cy={cy + 2} rx="11" ry="13" fill={INK} />
          <circle cx={cx + 3} cy={cy - 3} r="4.8" fill="#fff" />
          <circle cx={cx - 3.5} cy={cy + 6} r="2.6" fill="#fff" />
        </>
      );
    case "wide":
    case "tiny":
    case "shock": {
      const rx = type === "shock" ? 15 : 14;
      const ry = type === "shock" ? 17 : 16;
      const pr = type === "wide" ? 6 : type === "tiny" ? 3.4 : 2.8;
      return (
        <>
          <ellipse cx={cx} cy={cy} rx={rx} ry={ry} fill="#fff" stroke={INK} strokeWidth="3" />
          <g className={dart ? "nm-pupil nm-dart" : "nm-pupil"}>
            <circle cx={cx} cy={cy + 1} r={pr} fill={INK} />
            {type === "wide" && <circle cx={cx + 2} cy={cy - 1} r="1.8" fill="#fff" />}
          </g>
        </>
      );
    }
    case "happy":
      return <path d={`M${cx - 11} ${cy + 4} Q${cx} ${cy - 13} ${cx + 11} ${cy + 4}`} {...round} strokeWidth="6" />;
    case "dizzy":
      return (
        <path
          d={`M${cx} ${cy} m-2 0 a2 2 0 1 1 4 0 a4 4 0 1 1 -8 0 a6 6 0 1 1 12 0 a8 8 0 1 1 -16 0 a10 10 0 1 1 20 0`}
          {...round}
          strokeWidth="3"
        />
      );
    case "closed":
      return <path d={`M${cx - 10} ${cy} Q${cx} ${cy + 8} ${cx + 10} ${cy}`} {...round} strokeWidth="5" />;
    case "half":
      return (
        <>
          <path d={`M${cx - 11} ${cy} A11 14.5 0 0 0 ${cx + 11} ${cy} Z`} fill={INK} />
          <path d={`M${cx - 13} ${cy} L${cx + 13} ${cy}`} {...round} strokeWidth="4" />
          <circle cx={cx + 4} cy={cy + 5} r="2.4" fill="#fff" />
        </>
      );
    default:
      return null;
  }
}

const BROWS = {
  determined: [62, 47, 73, 47, 86, 51],
  worried: [62, 50, 73, 44, 86, 42],
  scared: [60, 44, 72, 36, 86, 36],
  panic: [58, 40, 71, 30, 86, 33],
  shock: [60, 38, 73, 28, 86, 34],
};

function Brows({ kind }) {
  if (!kind) return null;
  if (kind === "skeptic") {
    return (
      <>
        <path d="M62 54 Q74 53 86 54" {...round} strokeWidth="5" />
        <path d="M114 44 Q126 33 138 41" {...round} strokeWidth="5" />
      </>
    );
  }
  const [x1, y1, cx, cy, x2, y2] = BROWS[kind];
  return (
    <>
      <path d={`M${x1} ${y1} Q${cx} ${cy} ${x2} ${y2}`} {...round} strokeWidth="5" />
      <path d={`M${mx(x1)} ${y1} Q${mx(cx)} ${cy} ${mx(x2)} ${y2}`} {...round} strokeWidth="5" />
    </>
  );
}

function Mouth({ kind }) {
  switch (kind) {
    case "smile":
      return <path d="M91 88 Q100 97 109 88" {...round} strokeWidth="4.5" />;
    case "flat":
      return <path d="M93 92 L107 92" {...round} strokeWidth="4.5" />;
    case "tongue":
      return (
        <>
          <ellipse cx="106" cy="95" rx="3.6" ry="4.2" fill="#fda4af" stroke={INK} strokeWidth="2" />
          <path d="M93 92 Q100 94 108 91" {...round} strokeWidth="4" />
        </>
      );
    case "wavy":
      return <path d="M87 93 q3.25 -4 6.5 0 t6.5 0 t6.5 0 t6.5 0" {...round} strokeWidth="3.5" />;
    case "grimace":
      return (
        <>
          <rect x="87" y="86" width="26" height="12" rx="5" fill="#fff" stroke={INK} strokeWidth="3" />
          <path d="M87.5 92 H112.5 M95.7 86.5 V97.5 M104.3 86.5 V97.5" stroke={INK} strokeWidth="2" />
        </>
      );
    case "scream":
      return (
        <>
          <ellipse cx="100" cy="95" rx="8" ry="11" fill={INK} />
          <ellipse cx="100" cy="101" rx="5" ry="3.4" fill="#fb7185" />
        </>
      );
    case "o":
      return <ellipse cx="100" cy="94" rx="6" ry="8" fill={INK} />;
    case "grin":
      return (
        <>
          <path d="M85 86 Q100 108 115 86 Z" fill={INK} stroke={INK} strokeWidth="2" strokeLinejoin="round" />
          <ellipse cx="100" cy="94" rx="5.5" ry="2.8" fill="#fb7185" />
        </>
      );
    case "smirk":
      return <path d="M91 93 Q101 95 110 88" {...round} strokeWidth="4" />;
    case "frown":
      return <path d="M91 96 Q100 88 109 96" {...round} strokeWidth="4.5" />;
    default:
      return null;
  }
}

function Lashes() {
  const s = { stroke: INK, strokeWidth: 3, strokeLinecap: "round" };
  return (
    <>
      <path d="M63 64 l-6 -4 M65 59 l-4 -6" {...s} />
      <path d="M137 64 l6 -4 M135 59 l4 -6" {...s} />
    </>
  );
}

// transform places the 200×222 face (anchored at 100,72) inside a body.
export function Face({ face, transform, showMouth = true }) {
  const lookCls = face.look ? `nm-look nm-look-${face.look}` : "nm-look";
  const blush = face.fx?.includes("blush");
  return (
    <g transform={transform}>
      {blush && (
        <>
          <ellipse cx="54" cy="84" rx="9" ry="5" fill="#fff" opacity=".28" />
          <ellipse cx="146" cy="84" rx="9" ry="5" fill="#fff" opacity=".28" />
        </>
      )}
      <Brows kind={face.brow} />
      <g className={face.blink ? "nm-eyes nm-blink" : "nm-eyes"}>
        <g className={lookCls}>
          <Eye c={L} type={face.eye} dart={face.dart} />
          <Eye c={R} type={face.eye} dart={face.dart} />
        </g>
      </g>
      {face.lashes && <Lashes />}
      {showMouth && <Mouth kind={face.mouth} />}
    </g>
  );
}

function Drop({ x, y, s = 1, className, style }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${s})`}>
      <g className={className} style={style}>
        <path d="M0 0 C-5 7 -7 11 -7 15 A7 7 0 0 0 7 15 C7 11 5 7 0 0Z" fill="#93c5fd" />
        <ellipse cx="-2.4" cy="13.5" rx="1.6" ry="2.6" fill="#fff" opacity=".85" />
      </g>
    </g>
  );
}

function Star({ x, y, r, delay }) {
  return (
    <path
      className="nm-twinkle"
      style={{ animationDelay: `${delay}s` }}
      d={`M${x} ${y - r} Q${x} ${y} ${x + r} ${y} Q${x} ${y} ${x} ${y + r} Q${x} ${y} ${x - r} ${y} Q${x} ${y} ${x} ${y - r}Z`}
      fill="#FBBF24"
    />
  );
}

const BURST = [
  [166, 16, 176, 3],
  [178, 26, 192, 17],
  [182, 40, 197, 38],
];

function Burst({ mirror }) {
  return BURST.map(([a, b, c, d], i) => (
    <path
      key={i}
      className={mirror ? "nm-burst nm-burst-r" : "nm-burst"}
      style={{ animationDelay: `${i * 0.06}s` }}
      d={`M${mirror ? mx(a) : a} ${b} L${mirror ? mx(c) : c} ${d}`}
      stroke="#FBBF24"
      strokeWidth="6"
      strokeLinecap="round"
    />
  ));
}

export function BackEffects({ face }) {
  if (!face.fx?.includes("sparkles")) return null;
  return (
    <>
      <Star x={14} y={98} r={9} delay={0} />
      <Star x={188} y={118} r={7} delay={0.4} />
      <Star x={28} y={22} r={6} delay={0.8} />
    </>
  );
}

export function FrontEffects({ face, tearAt = [140, 112] }) {
  const fx = new Set(face.fx || []);
  return (
    <>
      {fx.has("burst") && <Burst />}
      {fx.has("burst2") && (
        <>
          <Burst />
          <Burst mirror />
        </>
      )}
      {fx.has("sweat1") && <Drop x={156} y={30} className="nm-drip" />}
      {fx.has("sweat2") && (
        <>
          <Drop x={156} y={30} className="nm-drip" />
          <Drop x={44} y={42} s={0.8} className="nm-drip nm-d2" />
        </>
      )}
      {fx.has("sweat3") && (
        <>
          <Drop x={160} y={26} className="nm-fling" style={{ "--fx": "10px", "--fy": "-8px" }} />
          <Drop x={40} y={30} s={0.85} className="nm-fling" style={{ "--fx": "-10px", "--fy": "-6px", animationDelay: ".2s" }} />
          <Drop x={170} y={66} s={0.75} className="nm-fling" style={{ "--fx": "12px", "--fy": "2px", animationDelay: ".35s" }} />
        </>
      )}
      {fx.has("tear") && <Drop x={tearAt[0]} y={tearAt[1]} s={0.75} className="nm-drip nm-slow" />}
    </>
  );
}

export function TypingDots() {
  return (
    <g>
      <rect x="4" y="2" width="46" height="24" rx="12" fill="#fff" stroke={INK} strokeWidth="2.5" />
      <path d="M20 25 L16 33 L28 25" fill="#fff" stroke={INK} strokeWidth="2.5" strokeLinejoin="round" />
      <rect x="18" y="23" width="12" height="4" fill="#fff" />
      <circle className="nm-dot" cx="16" cy="14" r="3.4" fill={INK} />
      <circle className="nm-dot" cx="27" cy="14" r="3.4" fill={INK} />
      <circle className="nm-dot" cx="38" cy="14" r="3.4" fill={INK} />
    </g>
  );
}

